// src/App.jsx
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import "./index.css";
import "./styles.css";

import AuthLayout from "./layouts/AuthLayout";
import AppLayout from "./layouts/AppLayout";
import ProtectedRoute from "./components/ProtectedRoute";

import Login from "./pages/Login";
import Logout from "./pages/Logout";
import HomeRedirect from "./pages/HomeRedirect";

import Reports from "./pages/Reports";
import EnvioMasivo from "./pages/EnvioMasivo";
import EnvioMasivoEmail from "./pages/EnvioMasivoEmail";

import DashboardApi from "./pages/DashboardApi";
import TemplatesPage from "./pages/Templates";
import PromotionsPage from "./pages/Promotions";
import SettingsMulti from "./pages/SettingsMulti";
import MailSettings from "./components/MailSettings";
import MailSend from "./components/MailSend";
import UsersAdmin from "./pages/UsersAdmin";
import LeadsBoard from "./pages/LeadsBoard";
import Chat from "./pages/Chat";
import HistoricoEnvios from "./pages/HistoricoEnvios";
import Bots from "./pages/Bots";
import SqliteViewer from "./pages/SqliteViewer";
import LogsPage from "./pages/LogsPage";
import FacebookPages from "./pages/FacebookPages";
import FacebookConnections from "./pages/MetaConnections";
import MessageTemplatePage from "./pages/MessageTemplatePage";
import AssignTemplatesToChannel from "./pages/AssignTemplatesToChannel";
import Contacts from "./pages/Contacts";
import { features } from "./config/features";

import InternalInbox from "./pages/InternalInbox";
import InternalThread from "./pages/InternalThread";
import BitacoraSearchPage from "./pages/BitacoraSearchPage";
import SiniestralidadPage from "./pages/SiniestralidadPage";
import SettingsTelegram from "./pages/SettingsTelegram";

import {
  AppointmentsListPage,
  AppointmentCreatePage,
  AppointmentEditPage,
  AppointmentAvailabilityPage,
  ServicesPage,
  LocationsPage,
  ResourcesPage,
  AppointmentReasonsPage,
  ScheduleRulesPage,
  ScheduleBlackoutsPage,
} from "./modules/appointments";

// ✅ WS global + toasts/badges
import { RealtimeProvider } from "./realtime/RealtimeProvider";

const FlowEditor = lazy(() => import("./pages/FlowEditor"));

export default function App() {
  return (
    <RealtimeProvider>
      <BrowserRouter>
        <Routes>
          {/* públicas */}
          <Route element={<AuthLayout />}>
            <Route path="/login" element={<Login />} />
            <Route path="/logout" element={<Logout />} />
          </Route>

          {/* protegidas + layout principal */}
          <Route
            element={
              <ProtectedRoute>
                <AppLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<HomeRedirect />} />

            {/* Envios Masivos */}
            <Route path="/whatsApp-web" element={<EnvioMasivo />} />
            <Route path="/envio-mail" element={<EnvioMasivoEmail />} />

            {/* Conexiones */}
            <Route path="/dashboard-api" element={<DashboardApi />} />
            <Route path="/dashboard-web/telegram" element={<SettingsTelegram />} />
            <Route path="/telegram" element={<SettingsTelegram />} />
            <Route path="/templates" element={<TemplatesPage />} />
            <Route path="/promociones" element={<PromotionsPage />} />
            <Route path="/settings" element={<SettingsMulti />} />

            {/* Facebook */}
            <Route path="/facebook/connections" element={<FacebookConnections />} />
            <Route path="/facebook/pages" element={<FacebookPages />} />

            {/* Mail */}
            <Route path="/mail/settings" element={<MailSettings />} />
            <Route path="/mail/send" element={<MailSend />} />

            {/* Admin */}
            <Route path="/admin/users" element={<UsersAdmin />} />
            <Route path="/logs" element={<LogsPage />} />
            <Route path="/admin/sqlite" element={<SqliteViewer />} />

            {/* Leads + Chat */}
            <Route path="/leads" element={<LeadsBoard />} />
            <Route path="/chat" element={<Chat />} />
            <Route path="/bitacoras" element={<BitacoraSearchPage />} />
            <Route path="/siniestralidad" element={<SiniestralidadPage />} />

            {/* Reportes */}
            <Route path="/reports" element={<Reports />} />
            <Route path="/reports/history" element={<HistoricoEnvios />} />

            {/* Bots */}
            <Route path="/bots" element={<Bots />} />
            {features.flow3 && (
              <>
                <Route
                  path="/flow-3"
                  element={
                    <Suspense fallback={null}>
                      <FlowEditor />
                    </Suspense>
                  }
                />
                <Route
                  path="/flow-3/:flowId"
                  element={
                    <Suspense fallback={null}>
                      <FlowEditor />
                    </Suspense>
                  }
                />
              </>
            )}

            {/* Appointments */}
            <Route path="/appointments" element={<AppointmentsListPage />} />
            <Route path="/appointments/new" element={<AppointmentCreatePage />} />
            <Route path="/appointments/:id" element={<AppointmentEditPage />} />
            <Route path="/appointments/availability" element={<AppointmentAvailabilityPage />} />

            {/* Masters / Catálogos relacionados a Appointments */}
            <Route path="/appointments/services" element={<ServicesPage />} />
            <Route path="/appointments/locations" element={<LocationsPage />} />
            <Route path="/appointments/resources" element={<ResourcesPage />} />
            <Route path="/appointments/reasons" element={<AppointmentReasonsPage />} />
            <Route path="/appointments/schedule-rules" element={<ScheduleRulesPage />} />
            <Route path="/appointments/schedule-blackouts" element={<ScheduleBlackoutsPage />} />

            {/* Plantillas mensajes */}
            <Route path="/messages/templates" element={<MessageTemplatePage />} />
            <Route path="/messages/chanels" element={<AssignTemplatesToChannel />} />

            {/* Contactos */}
            <Route path="/contactos" element={<Contacts />} />
            <Route path="/Contactos" element={<Contacts />} />

            {/* Interno */}
            <Route path="/internal" element={<InternalInbox />} />
            <Route path="/internal/:id" element={<InternalThread />} />
          </Route>

          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </BrowserRouter>
    </RealtimeProvider>
  );
}
